// src/lib/sam-api/transformer.ts - ENHANCED FOR MAXIMUM OPERATIONAL DATA EXTRACTION
import { SamContractResponse } from '../../types/sam';

export class SamContractTransformer {
  /**
   * Transform SAM.gov opportunity data with maximum operational data extraction
   */
  static async transform(data: any): Promise<SamContractResponse | null> {
    if (!data || !data.noticeId) {
      console.warn('Invalid contract data - missing noticeId');
      return null;
    }

    console.log(`🔄 Transforming contract: ${data.noticeId}`);

    try {
      // ENHANCED: Operational data extraction prioritized
      const description = await this.getEnhancedDescription(data);
      const contactInfo = this.getEnhancedContactInformation(data);
      const documents = this.getEnhancedDocuments(data);
      const agencyInfo = this.getComprehensiveAgencyInfo(data);
      const locationInfo = this.getDetailedLocationInfo(data);
      const classificationInfo = this.getAdvancedClassification(data);
      const solicitationDetails = this.getDetailedSolicitationInfo(data);

      const contract: SamContractResponse = {
        // Basic identification
        noticeId: data.noticeId || data.notice_id || data.id,
        notice_id: data.noticeId || data.notice_id || data.id,
        title: data.title || 'Untitled Contract',
        
        // ENHANCED: Better type determination
        type: this.determineContractType(data),
        baseType: data.baseType || data.base_type,
        status: this.mapStatus(data.active),
        
        // ENHANCED: Complete date information
        postedDate: this.safeDate(data.postedDate) || this.safeDate(data.posted_date) || new Date().toISOString(),
        updatedPublishedDate: this.safeDate(data.modifiedDate) || this.safeDate(data.updated_published_date) || new Date().toISOString(),
        responseDeadline: this.safeDate(data.responseDeadLine) || this.safeDate(data.dueDate) || this.safeDate(data.responseDeadline),
        dueDate: this.safeDate(data.responseDeadLine) || this.safeDate(data.dueDate) || this.safeDate(data.responseDeadline),
        archiveDate: this.safeDate(data.archiveDate),
        archiveType: data.archiveType,
        
        // ENHANCED: Complete identification
        solicitationNumber: data.solicitationNumber || data.solicitation_number || data.noticeId,
        contractNumber: data.contractNumber || data.contract_number,
        
        // ENHANCED: Comprehensive agency information
        agency: agencyInfo.name,
        department: agencyInfo.department,
        subTier: agencyInfo.subTier,
        office: agencyInfo.office,
        fullParentPathName: agencyInfo.fullHierarchy,
        fullParentPathCode: agencyInfo.hierarchyCodes,
        organizationType: agencyInfo.organizationType,
        organizationId: agencyInfo.organizationId,
        organizationLocationId: agencyInfo.locationId,
        
        // ENHANCED: Complete contact information
        contractingOfficer: contactInfo?.primary ? {
          name: contactInfo.primary.name,
          email: contactInfo.primary.email,
          phone: contactInfo.primary.phone,
          fax: contactInfo.primary.fax,
          title: contactInfo.primary.title
        } : undefined,
        point_of_contact: contactInfo,
        
        // ENHANCED: Advanced classification
        naicsCode: classificationInfo.primaryNaics,
        naicsCodes: classificationInfo.allNaicsCodes,
        pscCode: classificationInfo.classificationCode,
        setAsides: classificationInfo.setAsides,
        typeOfSetAside: classificationInfo.setAsideType,
        typeOfSetAsideDescription: classificationInfo.setAsideDescription,
        classification: data.classification,
        clearanceRequired: data.securityClearance || data.clearance_required,
        
        // ENHANCED: Detailed location information
        placeOfPerformance: locationInfo.performanceLocation,
        office_address: locationInfo.contractingOffice,
        
        // ENHANCED: Rich description and content
        description: description,
        requirements: this.extractRequirements(data),
        evaluationCriteria: this.extractEvaluationCriteria(data),
        requiredCertifications: this.extractRequiredCertifications(data),
        subcontractingGoals: this.extractSubcontractingGoals(data),
        
        // ENHANCED: Comprehensive documents and links
        documents: documents,
        resourceLinks: this.getResourceLinks(data),
        additionalInfoLink: data.additionalInfoLink,
        solicitationUrl: data.uiLink || data.link || data.solicitationUrl,
        
        // ENHANCED: Detailed solicitation information
        response_type: solicitationDetails.responseType,
        response_instructions: solicitationDetails.responseInstructions,
        questions_deadline: solicitationDetails.questionsDeadline,
        questions_allowed: solicitationDetails.questionsAllowed,
        solicitation_deadlines: solicitationDetails.deadlines,
        additional_reporting: solicitationDetails.additionalReporting,
        
        // ENHANCED: Contract details
        isRecompete: this.parseBoolean(data.isRecompete) || this.inferRecompete(data),
        incumbentContractor: data.incumbentContractor || data.incumbent_contractor,
        totalBidders: data.totalBidders || data.total_bidders,
        periodOfPerformance: this.getPeriodOfPerformance(data),
        
        // ENHANCED: Links and references
        links: this.getLinks(data),
        pointOfContact: Array.isArray(data.pointOfContact) ? data.pointOfContact : (data.pointOfContact ? [data.pointOfContact] : []),
        
        // Quality and matching
        winScore: this.calculateWinScore(data),
        matchScore: this.calculateMatchScore(data),
        
        // ENHANCED: Operational quality assessment
        extraction_quality: this.assessOperationalQuality(data, description, contactInfo, documents, agencyInfo, locationInfo),
        
        // Metadata for debugging
        metadata: {
          originalData: data,
          transformedAt: new Date().toISOString(),
          extractionVersion: '3.0-operational'
        }
      };

      console.log(`✅ Contract transformed successfully. Quality: ${contract.extraction_quality}`);
      return contract;

    } catch (error) {
      console.error('❌ Error transforming contract:', error);
      return null;
    }
  }

  /**
   * ENHANCED: Multi-source description extraction with intelligent prioritization
   */
  private static async getEnhancedDescription(data: any): Promise<string> {
    console.log('🔍 Enhanced description extraction started...');
    
    // Priority order for description sources (operational focus)
    const descriptionSources = [
      'description',           // Primary description field
      'additionalInfoText',    // Extended description 
      'content',              // Content field
      'details',              // Details field
      'summary',              // Summary field
      'requirements',         // Requirements text
      'statementOfWork',      // Statement of work
      'scope',               // Scope of work
      'overview',            // Overview text
      'solicitation',        // Solicitation details
      'opportunity'          // Opportunity details
    ];

    let description = '';
    
    // Try each description source in priority order
    for (const source of descriptionSources) {
      let sourceText = '';
      
      // Handle nested objects for solicitation/opportunity
      if (source === 'solicitation' && data.solicitation) {
        sourceText = data.solicitation.description || data.solicitation.details || '';
      } else if (source === 'opportunity' && data.opportunity) {
        sourceText = data.opportunity.description || data.opportunity.details || '';
      } else {
        sourceText = data[source] || '';
      }
      
      if (sourceText && typeof sourceText === 'string' && sourceText.trim().length > 0) {
        // Clean up common SAM.gov formatting issues
        let cleanText = sourceText
          .replace(/\r\n/g, '\n')
          .replace(/\n{3,}/g, '\n\n')
          .replace(/\s{2,}/g, ' ')
          .trim();
        
        // Skip if it's just a URL, null string, or seems like an error message
        if (cleanText.length > 50 && 
            cleanText !== 'null' &&
            !cleanText.toLowerCase().includes('not found') &&
            !cleanText.toLowerCase().includes('error') &&
            !cleanText.toLowerCase().includes('unavailable') &&
            !cleanText.startsWith('http')) {
          description = cleanText;
          console.log(`✅ Found rich description from ${source}: ${description.length} characters`);
          break;
        }
      }
    }

    // If no good description found, build operational summary from metadata
    if (!description || description.length < 50) {
      console.log('⚠️ Building operational description from metadata...');
      const parts = [];
      
      if (data.title) {
        parts.push(`**Contract:** ${data.title}`);
      }
      
      if (data.department) {
        parts.push(`**Department:** ${data.department}`);
      }
      
      if (data.subTier) {
        parts.push(`**Office:** ${data.subTier}`);
      }
      
      if (data.naicsCode) {
        parts.push(`**NAICS:** ${data.naicsCode}`);
      }
      
      if (data.typeOfSetAsideDescription) {
        parts.push(`**Set-Aside:** ${data.typeOfSetAsideDescription}`);
      }
      
      if (data.pointOfContact && data.pointOfContact.length > 0) {
        const contact = data.pointOfContact[0];
        if (contact.fullName) {
          parts.push(`**Contact:** ${contact.fullName}`);
        }
        if (contact.email) {
          parts.push(`**Email:** ${contact.email}`);
        }
      }
      
      description = parts.join('\n\n');
      console.log(`✅ Built operational description from metadata: ${description.length} characters`);
    }

    return description || 'Contract details available - view full solicitation for complete information';
  }

  /**
   * ENHANCED: Complete contact information extraction
   */
  private static getEnhancedContactInformation(data: any): any {
    console.log('🔍 Enhanced contact information extraction...');
    
    // Handle multiple contact structure formats from SAM.gov
    let contacts = data.pointOfContact || data.contacts || data.contact || [];
    
    // Ensure contacts is an array
    if (!Array.isArray(contacts)) {
      contacts = [contacts];
    }

    if (contacts.length === 0) {
      console.log('⚠️ No contacts found in data');
      return null;
    }

    const contactInfo: any = {};

    // Extract primary contact with enhanced field mapping
    const primaryContact = contacts.find((c: any) => 
      c.type === 'primary' || 
      c.isPrimary === true || 
      c.contactType === 'primary' ||
      c.fullName || c.name || c.email
    ) || contacts[0];

    if (primaryContact) {
      // Enhanced name extraction
      let name = primaryContact.fullName || 
                 primaryContact.name || 
                 primaryContact.contactName ||
                 (primaryContact.firstName && primaryContact.lastName ? 
                  `${primaryContact.firstName} ${primaryContact.lastName}` : '');

      // Handle edge case from debug output where phone is in fullName
      if (name && name.toLowerCase().includes('telephone:')) {
        const phoneMatch = name.match(/telephone:\s*(\d+)/i);
        if (phoneMatch && !primaryContact.phone) {
          primaryContact.phone = phoneMatch[1];
        }
        name = name.replace(/telephone:\s*\d+/i, '').trim() || 'Contact Name Not Available';
      }
      
      name = name || 'Contact Name Not Available';
                   
      contactInfo.primary = {
        name: name,
        title: primaryContact.title || primaryContact.jobTitle || primaryContact.position || primaryContact.role || '',
        email: primaryContact.email || primaryContact.emailAddress || primaryContact.contactEmail || '',
        phone: primaryContact.phone || primaryContact.phoneNumber || primaryContact.telephone || primaryContact.tel || '',
        fax: primaryContact.fax || primaryContact.faxNumber || '',
        additionalInfo: primaryContact.additionalInfo || null
      };
      
      console.log('✅ Primary contact extracted:', contactInfo.primary);
    }

    // Extract secondary contact
    const secondaryContact = contacts.find((c: any) => 
      c.type === 'secondary' || 
      c.contactType === 'secondary'
    ) || (contacts.length > 1 ? contacts[1] : null);

    if (secondaryContact && secondaryContact !== primaryContact) {
      const name = secondaryContact.fullName || 
                   secondaryContact.name || 
                   secondaryContact.contactName ||
                   (secondaryContact.firstName && secondaryContact.lastName ? 
                    `${secondaryContact.firstName} ${secondaryContact.lastName}` : '') ||
                   'Secondary Contact';
                   
      contactInfo.secondary = {
        name: name,
        title: secondaryContact.title || secondaryContact.jobTitle || secondaryContact.position || secondaryContact.role || '',
        email: secondaryContact.email || secondaryContact.emailAddress || secondaryContact.contactEmail || '',
        phone: secondaryContact.phone || secondaryContact.phoneNumber || secondaryContact.telephone || secondaryContact.tel || '',
        fax: secondaryContact.fax || secondaryContact.faxNumber || '',
        additionalInfo: secondaryContact.additionalInfo || null
      };
      
      console.log('✅ Secondary contact extracted:', contactInfo.secondary);
    }

    // Add all additional contacts for comprehensive coverage
    const additionalContacts = contacts.filter((c: any, index: number) => 
      index > 1 || (c.type && c.type !== 'primary' && c.type !== 'secondary')
    );

    if (additionalContacts.length > 0) {
      contactInfo.additional = additionalContacts.map((contact: any) => ({
        name: contact.fullName || contact.name || contact.contactName || 'Additional Contact',
        title: contact.title || contact.jobTitle || contact.position || contact.role || '',
        email: contact.email || contact.emailAddress || contact.contactEmail || '',
        phone: contact.phone || contact.phoneNumber || contact.telephone || contact.tel || '',
        fax: contact.fax || contact.faxNumber || '',
        type: contact.type || 'additional',
        additionalInfo: contact.additionalInfo || null
      }));
      
      console.log(`✅ ${additionalContacts.length} additional contacts extracted`);
    }

    // Add organizational contact info
    if (data.department) contactInfo.department = data.department;
    if (data.subTier) contactInfo.sub_tier = data.subTier;
    if (data.office) contactInfo.office = data.office;

    return Object.keys(contactInfo).length > 0 ? contactInfo : null;
  }

  /**
   * ENHANCED: Comprehensive document and attachment extraction
   */
  private static getEnhancedDocuments(data: any): any[] {
    const documents: any[] = [];
    
    console.log('🔍 Enhanced document extraction from SAM.gov data...');
    
    // Extract from all possible SAM.gov document sources
    const documentSources = [
      { field: 'resourceLinks', name: 'Resource Document', priority: 1 },
      { field: 'attachments', name: 'Attachment', priority: 2 },
      { field: 'documents', name: 'Document', priority: 3 },
      { field: 'links', name: 'Related Link', priority: 4 },
      { field: 'additionalInfoLink', name: 'Additional Information', priority: 5 },
      { field: 'resources', name: 'Resource File', priority: 6 },
      { field: 'files', name: 'File', priority: 7 },
      { field: 'solicitation', name: 'Solicitation Document', priority: 8 },
      { field: 'uiLink', name: 'SAM.gov Link', priority: 9 }
    ];

    documentSources.forEach(source => {
      const fieldData = data[source.field];
      
      if (Array.isArray(fieldData) && fieldData.length > 0) {
        console.log(`📎 Found ${fieldData.length} ${source.field}`);
        fieldData.forEach((item, index) => {
          if (typeof item === 'string' && item.startsWith('http') && item !== 'null') {
            // Simple URL string
            documents.push({
              name: `${source.name} ${index + 1}`,
              url: item,
              type: source.field,
              priority: source.priority,
              datePosted: data.postedDate || data.posted_date || new Date().toISOString()
            });
          } else if (typeof item === 'object' && (item.url || item.link || item.href)) {
            // Object with URL
            const url = item.url || item.link || item.href;
            if (url && url.startsWith('http') && url !== 'null') {
              documents.push({
                name: item.name || item.title || item.description || `${source.name} ${index + 1}`,
                url: url,
                type: source.field,
                priority: source.priority,
                description: item.description || item.summary || '',
                datePosted: item.datePosted || data.postedDate || data.posted_date || new Date().toISOString(),
                size: item.size || null,
                format: item.format || this.inferFileFormat(url)
              });
            }
          }
        });
      } else if (typeof fieldData === 'string' && fieldData.startsWith('http') && fieldData !== 'null') {
        // Single URL string
        console.log(`📎 Found single ${source.field}`);
        documents.push({
          name: source.name,
          url: fieldData,
          type: source.field,
          priority: source.priority,
          datePosted: data.postedDate || data.posted_date || new Date().toISOString(),
          format: this.inferFileFormat(fieldData)
        });
      }
    });

    // Sort documents by priority and remove duplicates
    const uniqueDocuments = documents
      .filter((doc, index, self) => 
        index === self.findIndex(d => d.url === doc.url)
      )
      .sort((a, b) => a.priority - b.priority);

    console.log(`✅ Total unique documents extracted: ${uniqueDocuments.length}`);
    return uniqueDocuments;
  }

  /**
   * ENHANCED: Comprehensive agency information extraction
   */
  private static getComprehensiveAgencyInfo(data: any): any {
    console.log('🔍 Enhanced agency information extraction...');
    
    return {
      name: data.agency || data.department || data.organization || 'Agency Not Specified',
      department: data.department || data.dept || '',
      subTier: data.subTier || data.sub_tier || data.office || '',
      office: data.office || data.contractingOffice || '',
      fullHierarchy: data.fullParentPathName || data.full_parent_path_name || '',
      hierarchyCodes: data.fullParentPathCode || data.full_parent_path_code || '',
      organizationType: data.organizationType || data.organization_type || '',
      organizationId: data.organizationId || data.organization_id || '',
      locationId: data.organizationLocationId || data.organization_location_id || ''
    };
  }

  /**
   * ENHANCED: Detailed location information extraction
   */
  private static getDetailedLocationInfo(data: any): any {
    console.log('🔍 Enhanced location information extraction...');
    
    // Enhanced place of performance
    const performanceLocation = data.placeOfPerformance || data.place_of_performance || data.location;
    let enhancedPerformanceLocation = null;
    
    if (performanceLocation) {
      enhancedPerformanceLocation = {
        streetAddress: performanceLocation.streetAddress || performanceLocation.street_address || '',
        streetAddress2: performanceLocation.streetAddress2 || performanceLocation.street_address_2 || '',
        city: performanceLocation.city?.name || performanceLocation.cityName || performanceLocation.city || '',
        cityCode: performanceLocation.city?.code || performanceLocation.city_code || '',
        state: performanceLocation.state?.name || performanceLocation.stateName || performanceLocation.state || '',
        stateCode: performanceLocation.state?.code || performanceLocation.stateCode || performanceLocation.state_code || '',
        country: performanceLocation.country?.name || performanceLocation.countryName || performanceLocation.country || 'United States',
        countryCode: performanceLocation.country?.code || performanceLocation.countryCode || performanceLocation.country_code || 'USA',
        zip: performanceLocation.zip || performanceLocation.zipCode || performanceLocation.zip_code || ''
      };
    }

    // Enhanced contracting office address
    const officeAddress = data.officeAddress || data.office_address || data.address;
    let enhancedOfficeAddress = null;
    
    if (officeAddress) {
      enhancedOfficeAddress = {
        street1: officeAddress.street1 || officeAddress.streetAddress || '',
        street2: officeAddress.street2 || '',
        city: officeAddress.city || '',
        state: officeAddress.state || '',
        zip: officeAddress.zip || officeAddress.zipcode || officeAddress.zipCode || '',
        country: officeAddress.country || 'United States',
        countryCode: officeAddress.countryCode || 'USA'
      };
    }

    return {
      performanceLocation: enhancedPerformanceLocation,
      contractingOffice: enhancedOfficeAddress
    };
  }

  /**
   * Map raw SAM.gov set-aside strings to standardized SetAsideType values
   */
  private static mapSamSetAsides(rawSetAsides: string[]): string[] {
    const setAsideMapping: Record<string, string> = {
      // Common SAM.gov set-aside mappings
      'SBA8A': '8(a)',
      '8A': '8(a)',
      '8AN': '8(a)',       // 8(a) sole source
      '8(A)': '8(a)',
      'HUBZ': 'HUBZone',
      'HZC': 'HUBZone',    // HUBZone set-aside
      'HZS': 'HUBZone',    // HUBZone sole source
      'HUBZONE': 'HUBZone',
      'HUBZone': 'HUBZone',
      'SB': 'SB',
      'SBP': 'SB',         // partial small business
      'SMALL_BUSINESS': 'SB',
      'Small Business': 'SB',
      'SDB': 'SDB',
      'SDVOSB': 'SDVOSB',
      'SDVO': 'SDVOSB',
      'SDVOSBC': 'SDVOSB', // SDVOSB set-aside
      'SDVOSBS': 'SDVOSB', // SDVOSB sole source
      'Service-Disabled Veteran-Owned Small Business': 'SDVOSB',
      // Veteran-Owned (NOT service-disabled) — VA "Veterans First". Previously these were
      // mis-mapped to SDVOSB by the keyword fallback below.
      'VOSB': 'VOSB',
      'VSA': 'VOSB',       // veteran-owned set-aside
      'VSS': 'VOSB',       // veteran-owned sole source
      'VSB': 'VOSB',
      'Veteran-Owned Small Business': 'VOSB',
      'WOSB': 'WOSB',
      'WOSBSS': 'WOSB',    // WOSB sole source
      'Women-Owned Small Business': 'WOSB',
      'EDWOSB': 'EDWOSB',
      'EDWOSBSS': 'EDWOSB', // EDWOSB sole source
      'Economically Disadvantaged Women-Owned Small Business': 'EDWOSB',
      // Handle variations and edge cases
      'NONE': '',
      'None': '',
      'N/A': '',
      'Not Applicable': ''
    };

    return rawSetAsides
      .map(setAside => {
        if (!setAside || typeof setAside !== 'string') return null;
        
        // Clean up the string
        const cleaned = setAside.trim();
        
        // Direct mapping
        if (setAsideMapping[cleaned]) {
          return setAsideMapping[cleaned];
        }
        
        // Case-insensitive mapping
        const upperCased = cleaned.toUpperCase();
        const found = Object.keys(setAsideMapping).find(key => 
          key.toUpperCase() === upperCased
        );
        
        if (found && setAsideMapping[found]) {
          return setAsideMapping[found];
        }
        
        // Partial matching for common patterns
        if (cleaned.toLowerCase().includes('8(a)') || cleaned.toLowerCase().includes('8a')) {
          return '8(a)';
        }
        if (cleaned.toLowerCase().includes('hubzone')) {
          return 'HUBZone';
        }
        if (cleaned.toLowerCase().includes('small business') && !cleaned.toLowerCase().includes('women')) {
          return 'SB';
        }
        if (cleaned.toLowerCase().includes('service-disabled') || cleaned.toLowerCase().includes('service disabled')) {
          return 'SDVOSB';
        }
        if (cleaned.toLowerCase().includes('veteran')) {
          return 'VOSB';
        }
        if (cleaned.toLowerCase().includes('women')) {
          if (cleaned.toLowerCase().includes('disadvantaged')) {
            return 'EDWOSB';
          }
          return 'WOSB';
        }
        
        // If no mapping found, return null to filter out
        console.warn(`Unknown set-aside type: "${cleaned}"`);
        return null;
      })
      .filter((setAside): setAside is string => setAside !== null && setAside !== '');
  }

  /**
   * ENHANCED: Advanced classification and set-aside extraction
   */
  private static getAdvancedClassification(data: any): any {
    console.log('🔍 Enhanced classification extraction...');
    
    // Primary NAICS
    const primaryNaics = data.naicsCode || data.naics_code || '';
    
    // All NAICS codes
    const allNaicsCodes = [];
    if (data.naics && Array.isArray(data.naics)) {
      data.naics.forEach((naicsObj: any) => {
        if (naicsObj.code && Array.isArray(naicsObj.code)) {
          naicsObj.code.forEach((code: string) => {
            allNaicsCodes.push({
              code: code,
              type: naicsObj.type || 'unknown'
            });
          });
        }
      });
    }
    
    // Add primary NAICS if not already included
    if (primaryNaics && !allNaicsCodes.find(n => n.code === primaryNaics)) {
      allNaicsCodes.unshift({
        code: primaryNaics,
        type: 'primary'
      });
    }

    // Set-aside information
    const setAsides = [];
    if (Array.isArray(data.setAsides)) setAsides.push(...data.setAsides);
    if (Array.isArray(data.set_asides)) setAsides.push(...data.set_asides);
    if (data.setAside) setAsides.push(data.setAside);
    if (data.set_aside) setAsides.push(data.set_aside);
    if (data.typeOfSetAside) setAsides.push(data.typeOfSetAside);

    // Map raw set-asides to standardized format
    const mappedSetAsides = this.mapSamSetAsides([...new Set(setAsides)].filter(sa => sa && typeof sa === 'string'));
    return {
      primaryNaics: primaryNaics,
      allNaicsCodes: allNaicsCodes,
      classificationCode: data.classificationCode || data.classification_code || data.pscCode || data.psc_code,
      setAsides: mappedSetAsides,
      setAsideType: data.typeOfSetAside || data.type_of_set_aside,
      setAsideDescription: data.typeOfSetAsideDescription || data.type_of_set_aside_description
    };
  }

  /**
   * ENHANCED: Detailed solicitation information extraction
   */
  private static getDetailedSolicitationInfo(data: any): any {
    console.log('🔍 Enhanced solicitation information extraction...');
    
    const solicitation = data.solicitation || {};
    const deadlines = solicitation.deadlines || {};
    
    return {
      responseType: this.inferResponseType(data),
      responseInstructions: this.extractResponseInstructions(data),
      questionsDeadline: this.safeDate(data.questionsDeadline) || this.safeDate(data.questions_deadline),
      questionsAllowed: this.parseBoolean(data.questionsAllowed) !== false,
      deadlines: {
        response: this.safeDate(deadlines.response) || this.safeDate(data.responseDeadLine),
        responseTimezone: deadlines.responseTz || deadlines.response_tz,
        questions: this.safeDate(data.questionsDeadline) || this.safeDate(data.questions_deadline)
      },
      additionalReporting: data.additionalReporting || data.additional_reporting || [],
      setAsideInfo: solicitation.setAside || solicitation.set_aside
    };
  }

  /**
   * ENHANCED: Operational quality assessment (non-financial focus)
   */
  private static assessOperationalQuality(data: any, description: string, contactInfo: any, documents: any[], agencyInfo: any, locationInfo: any): string {
    let score = 0;
    
    // Description quality (0-3 points)
    if (description.length > 500) score += 3;
    else if (description.length > 200) score += 2;
    else if (description.length > 100) score += 1;
    
    // Contact information quality (0-4 points) - HIGH PRIORITY
    if (contactInfo?.primary?.email) score += 2;
    if (contactInfo?.primary?.phone) score += 1;
    if (contactInfo?.primary?.title) score += 1;
    
    // Document availability (0-3 points) - HIGH PRIORITY
    if (documents.length > 5) score += 3;
    else if (documents.length > 2) score += 2;
    else if (documents.length > 0) score += 1;
    
    // Agency information completeness (0-3 points)
    if (agencyInfo.department) score += 1;
    if (agencyInfo.subTier) score += 1;
    if (agencyInfo.fullHierarchy) score += 1;
    
    // Location information (0-2 points)
    if (locationInfo.performanceLocation?.city) score += 1;
    if (locationInfo.performanceLocation?.state) score += 1;
    
    // Additional operational indicators (0-3 points)
    if (data.naicsCode) score += 1;
    if (data.responseDeadLine || data.responseDeadline) score += 1;
    if (data.type && data.type !== 'Unknown') score += 1;
    
    // Operational quality assessment based on total score (0-19 possible)
    if (score >= 15) return 'Excellent';
    if (score >= 10) return 'Good';
    if (score >= 6) return 'Fair';
    return 'Poor';
  }

  /**
   * Helper methods (enhanced versions)
   */
  private static determineContractType(data: any): string {
    // Enhanced type determination
    if (data.type && typeof data.type === 'string') {
      return data.type;
    }

    // Check base type
    if (data.baseType && typeof data.baseType === 'string') {
      return data.baseType;
    }

    // Infer from notice type with expanded mapping
    const noticeType = data.noticeType || data.notice_type || '';
    if (noticeType) {
      const typeMap: Record<string, string> = {
        'o': 'Solicitation',
        'k': 'Combined Synopsis/Solicitation', 
        'p': 'Presolicitation',
        'r': 'Sources Sought',
        'g': 'Special Notice',
        's': 'Sale of Surplus Property',
        'a': 'Award Notice',
        'i': 'Intent to Bundle',
        'u': 'Justification',
        'c': 'Contract Award'
      };
      
      if (typeMap[noticeType.toLowerCase()]) {
        return typeMap[noticeType.toLowerCase()];
      }
    }

    // Enhanced inference from title or description
    const title = (data.title || '').toLowerCase();
    const description = (data.description || '').toLowerCase();
    
    if (title.includes('rfp') || description.includes('rfp')) {
      return 'Request for Proposal (RFP)';
    }
    if (title.includes('rfq') || description.includes('rfq')) {
      return 'Request for Quote (RFQ)';
    }
    if (title.includes('rfi') || description.includes('rfi')) {
      return 'Request for Information (RFI)';
    }
    if (title.includes('sources sought') || description.includes('sources sought')) {
      return 'Sources Sought';
    }
    if (title.includes('award') || description.includes('award')) {
      return 'Award Notice';
    }

    return 'Solicitation';
  }

  private static getResourceLinks(data: any): any[] {
    const links = [];
    
    if (Array.isArray(data.resourceLinks)) {
      links.push(...data.resourceLinks);
    }
    
    return links;
  }

  private static getLinks(data: any): Record<string, string> {
    const links: Record<string, string> = {};
    
    if (data.uiLink && data.uiLink !== 'null') links.solicitation = data.uiLink;
    if (data.additionalInfoLink && data.additionalInfoLink !== 'null') links.additionalInfo = data.additionalInfoLink;
    if (data.link && data.link !== 'null') links.main = data.link;
    
    return links;
  }

  private static inferFileFormat(url: string): string {
    const extension = url.split('.').pop()?.toLowerCase();
    const formatMap: Record<string, string> = {
      'pdf': 'PDF',
      'doc': 'Word Document',
      'docx': 'Word Document',
      'xls': 'Excel Spreadsheet',
      'xlsx': 'Excel Spreadsheet',
      'txt': 'Text File',
      'zip': 'Archive',
      'rar': 'Archive'
    };
    
    return formatMap[extension || ''] || 'Document';
  }

  private static extractRequirements(data: any): string | null {
    return data.requirements || data.specifications || data.scope || null;
  }

  private static extractEvaluationCriteria(data: any): string | null {
    return data.evaluationCriteria || data.evaluation_criteria || null;
  }

  private static extractRequiredCertifications(data: any): string[] {
    const certifications = [];
    
    if (Array.isArray(data.requiredCertifications)) {
      certifications.push(...data.requiredCertifications);
    }
    
    // Extract from description
    const description = data.description || '';
    const certPatterns = [
      /security clearance/gi,
      /iso\s?\d+/gi,
      /cmmi/gi,
      /8\(a\)/gi,
      /small business/gi
    ];
    
    for (const pattern of certPatterns) {
      const matches = description.match(pattern);
      if (matches) {
        certifications.push(...matches);
      }
    }
    
    return [...new Set(certifications)];
  }

  private static extractSubcontractingGoals(data: any): any {
    return data.subcontractingGoals || data.subcontracting_goals || null;
  }

  private static getPeriodOfPerformance(data: any): any {
    return data.periodOfPerformance || data.period_of_performance || null;
  }

  private static inferResponseType(data: any): string | null {
    const description = (data.description || '').toLowerCase();
    const title = (data.title || '').toLowerCase();
    const type = (data.type || '').toLowerCase();
    
    if (description.includes('rfp') || title.includes('rfp')) return 'RFP';
    if (description.includes('rfq') || title.includes('rfq')) return 'RFQ';
    if (description.includes('rfi') || title.includes('rfi')) return 'RFI';
    if (type.includes('sources sought')) return 'Sources Sought';
    if (type.includes('special notice')) return 'Special Notice';
    
    return null;
  }

  private static extractResponseInstructions(data: any): string | null {
    if (data.responseInstructions) return data.responseInstructions;
    if (data.response_instructions) return data.response_instructions;
    
    const description = data.description || '';
    
    // Look for instruction patterns
    const instructionPatterns = [
      /responses?\s+must\s+be\s+([^.]+)/i,
      /submit\s+([^.]+)/i,
      /interested\s+parties\s+([^.]+)/i,
      /vendors?\s+must\s+([^.]+)/i
    ];
    
    for (const pattern of instructionPatterns) {
      const match = description.match(pattern);
      if (match) {
        return match[0];
      }
    }
    
    return null;
  }

  private static calculateWinScore(data: any): number {
    // Placeholder - implement actual win score calculation
    return Math.floor(Math.random() * 100);
  }

  private static calculateMatchScore(data: any): number {
    // Placeholder - implement actual match score calculation
    return Math.floor(Math.random() * 100);
  }

  private static inferRecompete(data: any): boolean {
    const title = (data.title || '').toLowerCase();
    const description = (data.description || '').toLowerCase();
    
    return title.includes('recompete') || 
           description.includes('recompete') ||
           title.includes('rebid') ||
           description.includes('rebid');
  }

  /**
   * Helper methods
   */
  private static safeDate(dateValue: any): string | null {
    if (!dateValue) return null;
    
    try {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date.toISOString();
    } catch {
      return null;
    }
  }

  private static parseBoolean(value: any): boolean | null {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const lower = value.toLowerCase();
      if (lower === 'true' || lower === 'yes' || lower === '1') return true;
      if (lower === 'false' || lower === 'no' || lower === '0') return false;
    }
    return null;
  }

  private static mapStatus(active: any): string {
    if (active === true || active === 'true' || active === 'yes' || active === 'Yes') return 'open';
    if (active === false || active === 'false' || active === 'no' || active === 'No') return 'closed';
    return 'pending';
  }
}

// Export compatibility aliases for existing code
export const SamDataTransformer = SamContractTransformer;
SamContractTransformer.transformToContract = SamContractTransformer.transform;